import { TPermissionKey } from 'store/user/slice';
import routes from '../router/routes';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/index';



type Route = Exclude<typeof routes[keyof typeof routes], '/dashboard' | '/login'>;

type TPermission = {
  [key in Route]: TPermissionKey;
}

const PermissionField: TPermission = {
  "/analytic/global/common": 'analytic_global_common',
  "/analytic/global/users": 'analytic_global_users',
  "/analytic/sports/categories": 'analytic_sports_categories',
  "/analytic/sports/competitions": 'analytic_sports_competitions',
  "/analytic/payments/methods": 'analytic_payments_methods',
  "/analytic/payments/providers": 'analytic_payments_providers',
  "/analytic/payments/deposits": 'analytic_payments_deposits',
  "/analytic/casino/games": 'analytic_casino_games',
  "/analytic/casino/providers": 'analytic_casino_providers',
  "/logs/errors": 'logs_errors',
  "/logs/warnings": 'logs_warnings',
  "/logs/notices": 'logs_notices',
  "/risk/permissions": 'risk_permissions',
  "/risk/limits": 'risk_limits',
  "/risk/players": 'risk_players',
  "/risk/kyc/documents": 'risk_kyc_documents',
  "/risk/kyc/presets": 'risk_kyc_presets',
  "/risk/kyc/rules": 'risk_kyc_rules',
  "/payments/psp/providers": 'payments_psp_providers',
  "/payments/psp/methods": 'payments_psp_methods',
  "/payments/transactions/operations": 'payments_transactions_operations',
  "/payments/transactions/deposits": 'payments_transactions_deposits',
  "/payments/transactions/withdrawals": 'payments_transactions_withdrawals',
  "/payments/transactions/compensation": 'payments_transactions_compensations',
  "/payments/transactions/compensations": 'payments_transactions_compensations',
  "/payments/transactions/confiscation": 'payments_transactions_confiscations',
  "/payments/transactions/confiscations": 'payments_transactions_confiscations',
  "/payments/currencies": 'payments_currencies',
  "/notifications/push/manual": 'notifications_push_manual',
  "/notifications/push/auto": 'notifications_push_auto',
  "/notifications/email/auto": 'notifications_email_auto',
  "/roles/list": 'roles_list',
  "/game-center/games/new": 'game_center_games_new',
  "/game-center/games/active": 'game_center_games_active',
  "/game-center/games/hidden": 'game_center_games_hidden',
  "/game-center/games/deactivated": 'game_center_games_deactivated',
  "/game-center/games/no-category": 'game_center_games_no_category',
  "/game-center/collections": 'game_center_collections',
  "/game-center/multi-scope": 'game_center_multi_scope',
  "/game-center/categories": 'game_center_categories',
  "/game-center/tags": 'game_center_tags',
  "/game-center/provider": 'game_center_provider',
  "/game-center/parsing": 'game_center_parsing',
  "/cms/widgets/main-page": 'cms_widgets_main_page',
  "/cms/widgets/casino-page": 'cms_widgets_casino_page',
  "/cms/widgets/live-casino": 'cms_widgets_live_casino',
  "/cms/widgets/burger-menu": 'cms_widgets_burger_menu',
  "/cms/widgets/mobile-menu": 'cms_widgets_mobile_menu',
  "/cms/widgets/sign-up-form": 'cms_widgets_sign_up_form',
  "/cms/widgets/pre-deposit-form": 'cms_widgets_pre_deposit_form',
  "/cms/widgets/profile-menu": 'cms_widgets_profile_menu',
  "/cms/widgets/game-bar": 'cms_widgets_game_bar',
  "/cms/widgets/header": 'cms_widgets_header',
  "/cms/widgets/footer": 'cms_widgets_footer',
  "/cms/banners": 'cms_banners',
  "/cms/pages/list": 'cms_pages_list',
  "/cms/pages/seo": 'cms_pages_seo',
  "/cms/pages/faq": 'cms_pages_faq',
  "/cms/promo": 'cms_promo',
  "/cms/footer/partners": 'cms_footer_partners',
  "/cms/footer/social-networks": 'cms_footer_social_networks',
  "/cms/footer/applications": 'cms_footer_applications',
  "/cms/footer/main-text": 'cms_footer_main_text',
  "/cms/footer/payments": 'cms_footer_payments',
  "/cms/footer/brand-core": 'cms_footer_brand_core',
  "/cms/footer/main-text/translation": 'cms_customized',
  "/cms/footer/main-text/set-translation": 'cms_customized',
  "/cms/customized": 'cms_customized',
  "/players/segments/manual": 'players_segments_manual',
  "/players/segments/auto": 'players_segments_auto',
  "/players/list": 'players_list',
  "/players/presets": 'players_presets',
  "/live-ops/wager-games/list": 'live_ops_wager_games_list',
  "/live-ops/wager-games/not-processing": 'live_ops_wager_games_not_processing',
  "/live-ops/bonuses/list": 'live_ops_bonuses_list',
  "/live-ops/bonuses/free-spins": 'live_ops_bonuses_free_spins',
  "/live-ops/loyalty-program/levels": 'live_ops_loyalty_program_levels',
  "/live-ops/loyalty-program/bets-points": 'live_ops_loyalty_program_bets_points',
  "/live-ops/loyalty-program/deposit-points": 'live_ops_loyalty_program_deposit_points',
  "/live-ops/loyalty-program/deposit-points-limits": 'live_ops_loyalty_program_deposit_points_limits',
  "/live-ops/loyalty-program/sign-up-points": 'live_ops_loyalty_program_sign_up_points',
  "/live-ops/loyalty-program/wager-points": 'live_ops_loyalty_program_wager_points',
  "/live-ops/cashbacks": 'live_ops_cashbacks',
  "/live-ops/tournaments": 'live_ops_tournaments',
  "/live-ops/promocodes/bonuses": 'live_ops_promocodes_bonuses',
  "/live-ops/cashback-games/list": 'live_ops_cashback_games_list',
  "/live-ops/cashback-games/not-processing": 'live_ops_cashback_games_not_processing',
  "/domains/zones": 'domains_zones',
  "/domains/mirrors": 'domains_mirrors',
  "/domains/countries": 'domains_countries',
  "/scheduler/list": 'scheduler_list',
  "/support/threads": 'support_threads',
  "/support/channels": 'support_channels',
} as const;

export const usePermissionHelper = (route?: Route) => {
  const isAuthorized = useAppSelector((store) => store.auth.isAuthorized);
  const permissions = useAppSelector((store) => store.auth.info?.permissions);
  const info = useAppSelector((store) => store.auth.info);
  const isAdmin = useAppSelector(
    (store) => store.auth.info?.roleName === "admin",
  );
  const { pathname } = useLocation();

  if (!isAuthorized || !info || !permissions) return null;

  const permissionKey = PermissionField[route ?? pathname as Route];
  const permissionByKey = permissions[permissionKey] ?? [];

  if (isAdmin) {
    return {
      read: true,
      create: true,
      // create: false,
      update: true,
      // update: false,
      delete: true,
      // delete: false,
      export: true,
      activePermissions: true,
      sensitive_data: true,
    }
  };

  return {
    read: permissionByKey.includes('read'),
    create: permissionByKey.includes('create'),
    update: permissionByKey.includes('update'),
    delete: permissionByKey.includes('delete'),
    export: permissionByKey.includes('export'),
    activePermissions: permissionByKey.includes('read'),
    sensitive_data: permissionByKey.includes('sensitive_data')
  }
};
